<script lang="ts">
  import { onMount } from 'svelte';
  import { settingsStorage } from '../services/storage';
  import { setFrappeApiUrl, getFrappeApiUrl, isFrappeConfigured } from '../config/frappe';
  import { frappeClient } from '../services/frappe';
  import { getLastInvoiceNumber } from '../utils/invoiceNumber';
  import { syncStore, isOnline, isSyncing, pendingItems } from '../stores/sync';
  import { formatDate } from '../utils/format';

  let frappeUrl = $state(getFrappeApiUrl());
  let sumupApiKey = $state('');
  let sumupMerchantCode = $state('');
  let frappeApiKey = $state('');
  let frappeApiSecret = $state('');
  let posId = $state('01');
  let lastInvoiceNumber = $state(0);
  let revenueAccount = $state('ACC-4000');
  let cashAccount = $state('ACC-2700');
  let sumupAccount = $state('ACC-2801');
  let paymentFeesAccount = $state('ACC-7790');
  let sumupFeePercentage = $state(1.39);
  let testMode = $state(false);
  let saveMessage = $state<string | null>(null);
  let showSyncDetails = $state(false);

  let status = $derived($syncStore);

  // Lade aktuelle Settings
  onMount(() => {
    loadSettings();
  });

  function loadSettings() {
    const settings = settingsStorage.load();
    frappeUrl = settings.frappe_url || getFrappeApiUrl();
    sumupApiKey = settings.sumup_api_key || '';
    sumupMerchantCode = settings.sumup_merchant_code || '';
    frappeApiKey = settings.frappe_api_key || '';
    frappeApiSecret = settings.frappe_api_secret || '';
    posId = settings.pos_id || '01';
    lastInvoiceNumber = settings.last_invoice_number || 0;
    revenueAccount = settings.revenue_account || 'ACC-4000';
    cashAccount = settings.cash_account || 'ACC-2700';
    sumupAccount = settings.sumup_account || 'ACC-2801';
    paymentFeesAccount = settings.payment_fees_account || 'ACC-7790';
    sumupFeePercentage = settings.sumup_fee_percentage || 1.39;
    testMode = settings.test_mode || false;
  }

  function handleSave() {
    try {
      // Speichere Frappe URL
      if (frappeUrl) {
        setFrappeApiUrl(frappeUrl);
        // Aktualisiere Frappe Client
        frappeClient.setBaseUrl(frappeUrl);
      }

      // Speichere SumUp Settings
      settingsStorage.update({
        sumup_api_key: sumupApiKey || undefined,
        sumup_merchant_code: sumupMerchantCode || undefined,
        frappe_api_key: frappeApiKey || undefined,
        frappe_api_secret: frappeApiSecret || undefined,
        pos_id: posId || '01',
        last_invoice_number: lastInvoiceNumber || 0,
        revenue_account: revenueAccount || 'ACC-4000',
        cash_account: cashAccount || 'ACC-2700',
        sumup_account: sumupAccount || 'ACC-2801',
        payment_fees_account: paymentFeesAccount || 'ACC-7790',
        sumup_fee_percentage: sumupFeePercentage || 1.39,
        test_mode: testMode
      });

      saveMessage = '✓ Einstellungen gespeichert!';
      setTimeout(() => {
        saveMessage = null;
      }, 3000);

      // Lade Settings neu
      loadSettings();
    } catch (error) {
      saveMessage =
        'Fehler beim Speichern: ' + (error instanceof Error ? error.message : 'Unbekannt');
    }
  }

  async function testConnection() {
    try {
      const isConnected = await frappeClient.ping();
      if (isConnected) {
        saveMessage = '✓ Verbindung erfolgreich!';
      } else {
        saveMessage = '✗ Verbindung fehlgeschlagen';
      }
      setTimeout(() => {
        saveMessage = null;
      }, 3000);
    } catch (error) {
      saveMessage = '✗ Fehler: ' + (error instanceof Error ? error.message : 'Unbekannt');
      setTimeout(() => {
        saveMessage = null;
      }, 3000);
    }
  }
</script>

<div class="min-h-screen bg-gray-50 p-4 md:p-6">
  <div class="max-w-4xl mx-auto">
    <!-- Header -->
    <div class="mb-6">
      <h1 class="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Einstellungen</h1>
      <p class="text-gray-600">Konfiguration für Backend, Zahlungen und Belegnummern</p>
    </div>

    <!-- Toast Message (sticky at top) -->
    {#if saveMessage}
      <div
        class="sticky top-4 z-50 mb-6 p-4 rounded-lg shadow-lg {saveMessage.startsWith('✓')
          ? 'bg-green-50 text-green-800 border border-green-200'
          : saveMessage.startsWith('✗')
            ? 'bg-red-50 text-red-800 border border-red-200'
            : 'bg-blue-50 text-blue-800 border border-blue-200'}"
      >
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <svg
              class="w-5 h-5 mr-2 {saveMessage.startsWith('✓')
                ? 'text-green-600'
                : saveMessage.startsWith('✗')
                  ? 'text-red-600'
                  : 'text-blue-600'}"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {#if saveMessage.startsWith('✓')}
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M5 13l4 4L19 7"
                />
              {:else if saveMessage.startsWith('✗')}
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              {:else}
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              {/if}
            </svg>
            <span class="font-medium">{saveMessage}</span>
          </div>
          <button
            onclick={() => (saveMessage = null)}
            class="ml-4 text-gray-500 hover:text-gray-700"
            aria-label="Schließen"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>
    {/if}

    <div class="space-y-6">
      <!-- Frappe Backend URL -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 class="text-lg font-semibold text-gray-900 mb-4">Backend-Konfiguration</h2>
        <div>
          <label for="frappe-url" class="block text-sm font-medium text-gray-700 mb-2">
            Frappe Backend URL *
          </label>
          <input
            id="frappe-url"
            type="url"
            bind:value={frappeUrl}
            placeholder="https://your-frappe-instance.com"
            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-900 bg-white placeholder:text-gray-400"
          />
          <p class="mt-1 text-xs text-gray-500">
            Die URL zu deinem Frappe Backend (ohne trailing slash)
          </p>
        </div>

        <div class="mt-6 grid md:grid-cols-2 gap-4">
          <div>
            <label for="frappe-api-key" class="block text-sm font-medium text-gray-700 mb-2">
              Frappe API Key
            </label>
            <input
              id="frappe-api-key"
              type="text"
              bind:value={frappeApiKey}
              placeholder="z.B. abc123"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-900 bg-white placeholder:text-gray-400"
            />
            <p class="mt-1 text-xs text-gray-500">
              API Key des technischen Benutzers (in Frappe unter User &gt; API Access).
            </p>
          </div>

          <div>
            <label for="frappe-api-secret" class="block text-sm font-medium text-gray-700 mb-2">
              Frappe API Secret
            </label>
            <input
              id="frappe-api-secret"
              type="password"
              bind:value={frappeApiSecret}
              placeholder="• • • • • •"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-900 bg-white placeholder:text-gray-400"
            />
            <p class="mt-1 text-xs text-gray-500">
              Wird verschlüsselt im Browser gespeichert. Nach dem Speichern werden alle Requests mit
              dem Token {`(token key:secret)`} authentifiziert.
            </p>
          </div>
        </div>
      </div>

      <!-- SumUp Konfiguration -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 class="text-lg font-semibold text-gray-900 mb-4">SumUp Integration</h2>
        <div class="space-y-4">
          <div>
            <label for="sumup-key" class="block text-sm font-medium text-gray-700 mb-2">
              SumUp API Key
            </label>
            <input
              id="sumup-key"
              type="password"
              bind:value={sumupApiKey}
              placeholder="Optional"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-900 bg-white placeholder:text-gray-400"
            />
          </div>

          <div>
            <label for="sumup-merchant" class="block text-sm font-medium text-gray-700 mb-2">
              SumUp Merchant Code
            </label>
            <input
              id="sumup-merchant"
              type="text"
              bind:value={sumupMerchantCode}
              placeholder="Optional"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-900 bg-white placeholder:text-gray-400"
            />
          </div>
        </div>
      </div>

      <!-- Belegnummern-Konfiguration -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 class="text-lg font-semibold text-gray-900 mb-4">Belegnummern</h2>
        <div class="space-y-4">
          <div>
            <label for="pos-id" class="block text-sm font-medium text-gray-700 mb-2">
              POS-ID
            </label>
            <input
              id="pos-id"
              type="text"
              bind:value={posId}
              placeholder="01"
              maxlength="2"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-900 bg-white placeholder:text-gray-400"
            />
            <p class="mt-1 text-xs text-gray-500">
              POS-ID für Belegnummern (2-stellig, z.B. "01"). Format: PS{'{'}TT{'}'}{'{'}MM{'}'}{'{'}YY{'}'}{'{'}POSID{'}'}{'{'}LfdNr{'}'}
            </p>
          </div>

          <div>
            <label for="last-invoice-number" class="block text-sm font-medium text-gray-700 mb-2">
              Letzte Belegnummer
            </label>
            <input
              id="last-invoice-number"
              type="number"
              bind:value={lastInvoiceNumber}
              min="0"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-900 bg-white placeholder:text-gray-400"
            />
            <p class="mt-1 text-xs text-gray-500">
              Letzte verwendete laufende Nummer (wird automatisch aktualisiert, startet bei
              Tageswechsel bei 1)
            </p>
          </div>
        </div>
      </div>

      <!-- Buchhaltungskonten -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 class="text-lg font-semibold text-gray-900 mb-4">Buchhaltungskonten</h2>
        <div class="space-y-4">
          <div>
            <label for="revenue-account" class="block text-sm font-medium text-gray-700 mb-2">
              Erlöskonto
            </label>
            <input
              id="revenue-account"
              type="text"
              bind:value={revenueAccount}
              placeholder="ACC-4000"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-900 bg-white placeholder:text-gray-400"
            />
            <p class="mt-1 text-xs text-gray-500">
              Konto für Verkaufserlöse (Credit bei Verkäufen)
            </p>
          </div>

          <div>
            <label for="cash-account" class="block text-sm font-medium text-gray-700 mb-2">
              Kasseneingangskonto
            </label>
            <input
              id="cash-account"
              type="text"
              bind:value={cashAccount}
              placeholder="ACC-2700"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-900 bg-white placeholder:text-gray-400"
            />
            <p class="mt-1 text-xs text-gray-500">
              Konto für Barzahlungen (Debit bei Barverkäufen)
            </p>
          </div>

          <div>
            <label for="sumup-account" class="block text-sm font-medium text-gray-700 mb-2">
              SumUp Konto
            </label>
            <input
              id="sumup-account"
              type="text"
              bind:value={sumupAccount}
              placeholder="ACC-2801"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-900 bg-white placeholder:text-gray-400"
            />
            <p class="mt-1 text-xs text-gray-500">
              Konto für Kartenzahlungen/SumUp (Debit bei Kartenverkäufen)
            </p>
          </div>

          <div>
            <label for="payment-fees-account" class="block text-sm font-medium text-gray-700 mb-2">
              Zahlungsverkehr Gebühren Konto
            </label>
            <input
              id="payment-fees-account"
              type="text"
              bind:value={paymentFeesAccount}
              placeholder="ACC-7790"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-900 bg-white placeholder:text-gray-400"
            />
            <p class="mt-1 text-xs text-gray-500">
              Konto für Zahlungsverkehrsgebühren (Debit bei SumUp-Gebühren)
            </p>
          </div>
        </div>
      </div>

      <!-- SumUp Gebühren -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 class="text-lg font-semibold text-gray-900 mb-4">SumUp Gebühren</h2>
        <div>
          <label for="sumup-fee-percentage" class="block text-sm font-medium text-gray-700 mb-2">
            SumUp Transaktionsgebühr (%)
          </label>
          <input
            id="sumup-fee-percentage"
            type="number"
            bind:value={sumupFeePercentage}
            min="0"
            max="100"
            step="0.01"
            placeholder="1.39"
            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-900 bg-white placeholder:text-gray-400"
          />
          <p class="mt-1 text-xs text-gray-500">
            Prozentsatz der SumUp Transaktionsgebühr (z.B. 1.39 für 1.39%)
          </p>
        </div>
      </div>

      <!-- Test Modus -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 class="text-lg font-semibold text-gray-900 mb-4">Test Modus</h2>
        <div class="flex items-center justify-between">
          <div>
            <label for="test-mode" class="block text-sm font-medium text-gray-700 mb-1">
              Test Modus aktivieren
            </label>
            <p class="text-xs text-gray-500">
              Wenn aktiv, werden alle Dokumente mit docstatus: 0 erstellt (Draft-Status)
            </p>
          </div>
          <label class="relative inline-flex items-center cursor-pointer">
            <input id="test-mode" type="checkbox" bind:checked={testMode} class="sr-only peer" />
            <div
              class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"
            ></div>
          </label>
        </div>
      </div>

      <!-- Status & Actions -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 class="text-lg font-semibold text-gray-900 mb-4">Status & Aktionen</h2>
        <div class="space-y-4">
          <div class="flex items-center justify-between py-2">
            <span class="text-sm font-medium text-gray-700">Backend-Status:</span>
            {#if isFrappeConfigured()}
              <span
                class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800"
              >
                <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                Konfiguriert
              </span>
            {:else}
              <span
                class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800"
              >
                <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                Nicht konfiguriert
              </span>
            {/if}
          </div>

          <!-- Sync Status Details -->
          <div class="pt-2 border-t border-gray-200">
            <div class="flex items-center justify-between mb-3">
              <span class="text-sm font-medium text-gray-700">Synchronisation:</span>
              <button
                onclick={() => (showSyncDetails = !showSyncDetails)}
                class="text-gray-500 hover:text-gray-700 text-sm"
              >
                {showSyncDetails ? 'Weniger' : 'Details'}
              </button>
            </div>

            <div class="flex items-center space-x-4 mb-3">
              {#if $isSyncing}
                <div class="flex items-center space-x-2 text-primary-600">
                  <svg
                    class="animate-spin h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    ></circle>
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <span class="text-sm">Synchronisiere...</span>
                </div>
              {/if}

              {#if $pendingItems > 0}
                <div class="flex items-center space-x-2 text-orange-600">
                  <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span class="text-sm font-medium">{$pendingItems} ausstehend</span>
                </div>
              {/if}
            </div>

            {#if showSyncDetails}
              <div class="mt-3 pt-3 border-t border-gray-200 space-y-2 text-sm text-gray-600">
                {#if status.lastSync}
                  <div>
                    <span class="font-medium">Letzte Synchronisation:</span>
                    <span class="ml-2">{formatDate(status.lastSync)}</span>
                  </div>
                {/if}

                {#if status.errors.length > 0}
                  <div class="text-red-600">
                    <span class="font-medium">Fehler:</span>
                    <span class="ml-2">{status.errors.length}</span>
                  </div>
                {/if}

                <div class="flex space-x-2 mt-3">
                  <button
                    onclick={() => syncStore.sync()}
                    disabled={$isSyncing || !$isOnline}
                    class="px-3 py-1 bg-primary-600 text-white rounded text-sm hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    Jetzt synchronisieren
                  </button>
                  {#if status.errors.length > 0}
                    <button
                      onclick={() => syncStore.retry()}
                      disabled={$isSyncing || !$isOnline}
                      class="px-3 py-1 bg-orange-600 text-white rounded text-sm hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      Fehler erneut versuchen
                    </button>
                  {/if}
                </div>
              </div>
            {/if}
          </div>

          <div class="flex gap-3 pt-2 border-t border-gray-200">
            <button
              onclick={testConnection}
              disabled={!frappeUrl || !frappeApiKey || !frappeApiSecret}
              class="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium disabled:bg-gray-300 disabled:cursor-not-allowed touch-manipulation"
            >
              Verbindung testen
            </button>
            <button
              onclick={handleSave}
              disabled={!frappeUrl}
              class="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium disabled:bg-gray-300 disabled:cursor-not-allowed touch-manipulation"
            >
              Alle Einstellungen speichern
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
